define('home/models/ChannelProfile',[
    'underscore',
    'backbone',

    'core/utils/storage',
    'core/UniqueModel',
    'core/strings',

    'home/models/Feed',
    'home/models/Channel',
    'home/collections/FeedItemCollection',
    'home/collections/UserCollection',
    'home/collections/ModeratorCollection',
    'home/collections/TopicCollection',
    'home/collections/SourceForumCollection',
    'home/utils/backboneUtils',

    'core/api',
], function (
    _,
    Backbone,

    storage,
    UniqueModel,
    strings,

    Feed,
    Channel,
    FeedItemCollection,
    UserCollection,
    ModeratorCollection,
    TopicCollection,
    SourceForumCollection,
    backboneUtils,

    api
) {
    'use strict';

    var gettext = strings.get;

    /**
     * View-model for channel pages
     */
    var ChannelProfile = Backbone.Model.extend({
        defaults: {
            channel: undefined, // Channel.prototype.idAttribute
        },

        initialize: function (_attributes, options) {
            options = options || {};

            this.initializeRelated(
                'channel',
                options,
                Channel
            );

            this.listenTo(this.channel, 'addModerator', function (user) {
                this.moderators.items.add(user);
            });

            this.listenTo(this.channel, 'removeModerator', function (user) {
                this.moderators.items.remove(user);
            });

            var channelId = this.channel.id;
            var target = 'channel:' + channelId;
            var fetchOptions;

            this.recentActivities = new Feed(options.recentActivitiesOptions, {
                collectionClass: FeedItemCollection,
                collectionOptions: {
                    type: 'profile',
                    target: target,
                },
            });

            // Allow us to manually test out the different sorts for top activities
            // Values:
            //   - None (don't pass) - Current sort method
            //   - hotPosts - Just uses posts to sort feed
            //   - hotRecommends - Just uses recommends to sort the feed
            //   - hotCombined - Equal weight to posts/recommends
            //   - hotCombinedHighRecommends - Highly weighted towards recommends, with a small amount of posts as a fallback
            if (storage.get('channelSort')) {
                options.topActivitiesOptions = options.topActivitiesOptions || {};
                options.topActivitiesOptions.fetchOptions = _.defaults({
                    sort: storage.get('channelSort'),
                }, options.topActivitiesOptions.fetchOptions);
            }

            this.topActivities = new Feed(options.topActivitiesOptions, {
                collectionClass: FeedItemCollection,
                collectionUrl: api.getURL('timelines/ranked'),
                collectionOptions: {
                    type: 'default',
                    target: target,
                },
            });

            this.sourceForums = new SourceForumCollection([], {
                channelId: this.channel.id,
            });

            fetchOptions = {
                channel: this.channel.id,
                limit: 10,
            };

            this.leaders = new Feed({
                fetchOptions: fetchOptions,
            }, {
                collectionClass: UserCollection,
                collectionUrl: api.getURL('channels/communityLeaders'),
            });

            // Fetch the moderators for the primary forum.
            // Wait unti channel has fetched to get the primary forum's id, if necessary
            if (this.channel.primaryForum) {
                fetchOptions = {
                    forum: this.channel.primaryForum.id,
                };
            } else {
                this.listenToOnce(this.channel, 'changeRelated:primaryForum', function () {
                    this.moderators.set({ fetchOptions: { forum: this.channel.primaryForum.id } });
                });
                fetchOptions = null;
            }

            // This list is shown in the moderators tab on a channel's page. It also has a side
            // effect when fetched, where it adds this channel's primary forum as a moderated
            // forum to the User model for each of the moderators. This results in Mod badges
            // showing up when those users appear on the channel page.
            this.moderators = new Feed({
                fetchOptions: fetchOptions,
            }, {
                collectionClass: ModeratorCollection,
                collectionOptions: {
                    channel: this.channel,
                },
                collectionUrl: api.getURL('forums/listModerators'),
            });

            this.trendingTopics = new Feed({
                fetchOptions: {
                    channel: this.channel.id,
                    limit: 15,
                },
            }, {
                collectionClass: TopicCollection,
                collectionUrl: api.getURL('channels/listTrendingTopics'),
            });

            // Mapping from topic id to Feed model.
            // Filled in lazily.
            this.topicFilteredFeeds = {};
        },

        toJSON: function () {
            var json = Backbone.Model.prototype.toJSON.apply(this, arguments);

            return _.defaults({
                channel: this.channel.toJSON(),
                recentActivities: this.recentActivities.toJSON(),
                topActivities: this.topActivities.toJSON(),
                trendingTopics: this.trendingTopics && this.trendingTopics.items.toJSON(),
            }, json);
        },

        getFetchedTopicFilteredFeed: function (topicId) {
            // TODO remove when releasing globally
            if (!this.trendingTopics)
                return;

            if (!this.topicFilteredFeeds[topicId]) {
                this.topicFilteredFeeds[topicId] = new Feed({
                    fetchOptions: {
                        topic: topicId,
                    },
                }, {
                    collectionClass: FeedItemCollection,
                    collectionUrl: api.getURL('timelines/ranked'),
                    collectionOptions: {
                        type: 'default',
                        target: 'channel:' + this.channel.id,
                    },
                });
            }

            var feed = this.topicFilteredFeeds[topicId];
            feed.ensureFetched();
            return feed;
        },

        ensureFetched: function () {
            this.channel.ensureFetchedWithCounters();

            this.recentActivities.ensureFetched();

            this.ensureFetchedTopActivities();

            this.ensureFetchedLeaders();

            this.ensureFetchedModerators();

            this.ensureFetchedTrendingTopics();
        },

        ensureFetchedTrendingTopics: function () {
            if (this.trendingTopics)
                this.trendingTopics.ensureFetched();
        },

        ensureFetchedTopActivities: function () {
            this.topActivities.ensureFetched();
        },

        ensureFetchedLeaders: function () {
            this.leaders.ensureFetched();
            this.leadersPromise = this.leaders.fetchPromise;
            return this.leadersPromise;
        },

        ensureFetchedModerators: function () {
            if (this.moderatorsPromise)
                return this.moderatorsPromise;

            if (this.moderators.get('fetchOptions')) {
                this.moderators.ensureFetched();
                this.moderatorsPromise = this.moderators.fetchPromise;
            } else {

                // If the fetch options haven't been built, wait until they are
                // before kicking off fetch.
                var moderators = this.moderators;
                this.moderatorsPromise = backboneUtils.getPromiseFor(moderators, 'fetchOptions').then(function () {
                    moderators.ensureFetched();
                    return moderators.fetchPromise;
                });
            }

            return this.moderatorsPromise.then(_.bind(this.markModerators, this));
        },

        ensureFetchedSourceForums: function () {
            if (!this.sourceForumsPromise)
                this.sourceForumsPromise = this.sourceForums.fetch();

            return this.sourceForumsPromise;
        },

        markModerators: function () {
            var forumId = this.channel.primaryForum.id;
            this.moderators.items.each(function (user) {
                var moderatedForums = user.get('moderatedForums') || [];
                // Must set moderatedForums to a new value instead of adding forumId to
                // the existing value in order for a change event to be triggered
                user.set({ moderatedForums: _.union(moderatedForums, [forumId]) });
            });
        },

        /**
         * Works the same way as validate, but since validate takes only current attributes
         * and not proposed changes, need a separate function to handle proposed additions
         * to the moderators list.
         */
        validateNewModerator: function (user) {
            if (!user.get('username').trim().length) {
                return {
                    attrName: 'username',
                    message: gettext('You must enter a username.'),
                };
            }

            if (this.moderators.items.contains(user)) {
                return {
                    attrName: 'username',
                    message: gettext('This user is already a moderator.'),
                };
            }
        },
    });

    UniqueModel.addType('ChannelProfile', ChannelProfile);

    return ChannelProfile;
});

